import { isDefined } from "@clipboard-health/util-ts";

import { hasFlexibleScheduleOptions, parseTimeInputValue } from "../../flexibleSchedule";
import {
  type CreateVisitRequest,
  isAutomaticOutreachDisabledForDiscipline,
  PricingType,
  VisitStatus,
  VisitType,
  type WorkerRequirements,
} from "../../types";
import { type VisitDraft } from "./visitDraft";

// useCreateVisit formats estimatedStartTime into the API's time string, so hand
// it a Date the way the desktop forms do.
type CreateVisitAttributes = Omit<
  CreateVisitRequest["data"]["attributes"],
  "estimatedStartTime"
> & {
  estimatedStartTime?: Date;
};

interface BuildCreateVisitAttributesParams {
  draft: VisitDraft;
  caseId: string;
  modificationReason: string;
  canSetRates: boolean;
  toDeadlineIso: (dateInput: string) => string;
}

/**
 * Convert a {@link VisitDraft} into createVisit attributes, mirroring the desktop
 * forms: schedule fields apply only to REGULAR visits, estimated duration only to
 * hourly, rates only when the poster can set them, and automatic outreach is
 * disabled for PT/OT/MSW disciplines.
 */
export function buildCreateVisitAttributes(
  params: BuildCreateVisitAttributesParams
): CreateVisitAttributes {
  const { draft, caseId, modificationReason, canSetRates, toDeadlineIso } = params;

  const visitType = draft.visitType as VisitType;
  const workerReq = (draft.workerRequirement as WorkerRequirements) || undefined;
  const isRegularVisit = visitType === VisitType.REGULAR;
  const isHourly = draft.pricingType === PricingType.PER_HOUR;

  const charge = Number.parseFloat(draft.chargePerVisit);
  const pay = Number.parseFloat(draft.payPerVisit);
  const estimatedDuration = Number.parseFloat(draft.estimatedDuration);

  const allowsFlexibleSchedule = hasFlexibleScheduleOptions({
    visitType,
    pricingType: draft.pricingType,
  });
  const estimatedStartTime = draft.hasFixedStartTime
    ? parseTimeInputValue(draft.estimatedStartTime)
    : undefined;

  return {
    status: VisitStatus.OPEN,
    caseId,
    type: visitType,
    workerReq,
    pricingType: draft.pricingType,
    interestedWorkersIds: [],
    invitedWorkersIds: [],
    invitedWorkersNames: [],
    modificationReason,
    ...(isAutomaticOutreachDisabledForDiscipline(workerReq)
      ? { automaticOutreachDisabled: true }
      : {}),
    ...(isRegularVisit && draft.visitsPerWeek
      ? { visitsPerWeek: Number.parseInt(draft.visitsPerWeek, 10) }
      : {}),
    ...(isRegularVisit && draft.totalWeeks
      ? { durationInWeeks: Number.parseInt(draft.totalWeeks, 10) }
      : {}),
    ...(draft.firstVisitDueDate ? { deadline: toDeadlineIso(draft.firstVisitDueDate) } : {}),
    ...(isHourly && Number.isFinite(estimatedDuration) ? { estimatedDuration } : {}),
    ...(allowsFlexibleSchedule && isDefined(estimatedStartTime) ? { estimatedStartTime } : {}),
    ...(allowsFlexibleSchedule && draft.hasSpecificDaysOfWeek && draft.daysOfWeek.length > 0
      ? { daysOfWeek: draft.daysOfWeek }
      : {}),
    ...(canSetRates && Number.isFinite(charge)
      ? {
          chargeRate: {
            amountInMinorUnits: Math.round(charge * 100),
            currencyCode: "USD" as const,
          },
        }
      : {}),
    ...(canSetRates && Number.isFinite(pay)
      ? {
          payRate: {
            amountInMinorUnits: Math.round(pay * 100),
            currencyCode: "USD" as const,
          },
        }
      : {}),
  };
}
